import { LogicalSpeaker } from '../hooks/useTranscription';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';

interface CorrectedSegment {
  content: string;
  speakerId: string;
  startTime: number;
  endTime: number;
}

interface DiarizationCorrectionData {
  id: string;
  confidence: number;
  originalContent: string;
  correctedSegments: CorrectedSegment[];
}

export function DiarizationCorrection({
  speakers,
  correction,
  onAccept,
  onReject,
  isProcessing = false,
}: {
  speakers: LogicalSpeaker[];
  correction: DiarizationCorrectionData;
  onAccept: () => void;
  onReject: () => void;
  isProcessing: boolean;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" variant="warn">
          <Icon className="lucide--triangle-alert" />
          <span>Possible speaker correction</span>
        </Button>
      </PopoverTrigger>

      <PopoverContent side="right" className="w-full max-w-md" sideOffset={12}>
        <div>
          <div className="-m-4 rounded-t-md border-b bg-gray-50 p-4">
            <p className="mb-1 font-medium">Speaker Correction</p>
            <p className="text-gray-600">Review recommended speaker assignment</p>
          </div>

          <div className="my-8">
            <p className="mb-4 text-gray-700">Recommended correction:</p>

            <div className="my-2 flex flex-col gap-2">
              {correction.correctedSegments.map((segment, index) => {
                // Find the speaker by UUID in the transcript speakers list
                const speaker = speakers.find((s) => s.id === segment.speakerId);
                const speakerName = speaker?.name ?? 'Unknown Speaker';

                const color = speaker?.color ?? 'gray';

                return (
                  <div key={index} className="flex items-start gap-3 rounded-md border border-gray-200 bg-gray-50 p-3">
                    <span
                      className={`rounded-md border px-2.5 py-1 text-xs font-medium bg-${color}-50 border-${color}-200 text-${color}-600`}
                    >
                      {speakerName}
                    </span>
                    <div className="flex-1">
                      <div className="text-sm leading-relaxed text-gray-900">{segment.content}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="-m-4 flex justify-end gap-2 rounded-b-md border-t bg-gray-50 p-4">
            <Button className="w-full" variant="destructive" onClick={onReject} disabled={isProcessing}>
              <Icon className="lucide--x" />
              <span>Reject</span>
            </Button>

            <Button className="w-full" variant="primary" onClick={onAccept} disabled={isProcessing}>
              <Icon className="lucide--check" />
              <span>Accept</span>
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
